import { createReducer, createSelector } from 'redux-starter-kit';
import { putDamageTypes, putDamageType } from './damageTypeActions';

/**
 * A selector for the damage types loaded in the domain state.
 * This is a dict object: { damageTypeCode: damageType}
 * @param {state} state The redux state of the application.
 *
 * @example const damageType = selectDamageTypeMap(state)[yourCode]
 */
export const selectDamageTypeMap = state => state.via.domain.damageTypes;

/**
 * A selector for the damage types loaded in the domain state.
 * This is a list: [ damageType, damageType, ...]
 * @param {object} state The redux state of the application.
 * @returns An array of damage type objects.
 * @example const damageTypes = selectDamageTypes(state);
 */
export const selectDamageTypes = createSelector(['via.domain.damageTypes'], map => (map ? Object.values(map) : null));

/**
 * A selector for a single damage type.
 * @param {object} state The redux state of the application.
 * @param {string} damageTypeCode The id of the damage type.
 * @returns The damage type object or null if it doesn't exist.
 * @example const damageType = selectDamageType(state, damageTypeCode);
 */
export const selectDamageType = (state, damageTypeCode) => {
  const damageTypeMap = selectDamageTypeMap(state);
  if (!damageTypeMap) return null;

  return damageTypeMap[damageTypeCode];
};

// A dict object with a property key of: damageTypeCode and a property value of: damageType.
const initialDamageTypes = {
  // damageTypeCode: { /* damage type object */ }
  // damageTypeCode: { /* damage type object */ }
  // ...etc
};

const damageTypesReducer = createReducer(initialDamageTypes, {
  // Insert/Replace all damage types.
  [putDamageTypes]: (state, action) => {
    const damageTypes = action.payload;
    return damageTypes.reduce((newState, type) => {
      newState[type.damageTypeCode] = type; // eslint-disable-line no-param-reassign
      return newState;
    }, { ...initialDamageTypes });
  },

  // Insert/Replace a single damage type.
  [putDamageType]: (state, action) => ({
    ...state,
    [action.payload.damageTypeCode]: action.payload,
  }),
});


export default damageTypesReducer;
